/**
 * NEXUS-MU API client.
 *
 * Talks to mu-api (sibling, port 3457) for layout, memory details, and edges.
 * Falls through to deterministic mock data when the API is unreachable so
 * `npm run dev` always renders something.
 */

export interface LayoutPoint {
  id: number
  x: number
  y: number
  z?: number | null
  cluster_id?: number | null
  importance?: number | null
  agent_id?: string | null
  topic?: string | null
  /** Shared id across every chunk of one ingested document. Drives the
   *  "DOCUMENT" group-by — all chunks of a document collapse to one group. */
  document_id?: string | null
}

export interface LayoutResponse {
  projection: 'umap' | 'tsne' | 'pca' | 'force'
  requestedProjection?: string
  points: LayoutPoint[]
  total: number
  computedAt: string
  elapsedMs?: number
  varianceExplained?: number[]
}

export interface LayoutQuery {
  projection?: 'umap' | 'tsne' | 'pca' | 'force'
  limit?: number
  agentId?: string
  importanceMin?: number
  importanceMax?: number
}

/**
 * Buckets for attachment rendering. The server stores `mimeType` (full
 * MIME) when known; this enum is what the UI uses to pick a viewer.
 *   image / video / audio → native HTML elements
 *   pdf                   → iframe (browsers render natively)
 *   model3d               → external link with hint (no embedded viewer v0)
 *   document              → text preview if small, else download link
 *   archive / data        → download link
 *   link                  → unstyled hyperlink
 */
export type AttachmentKind =
  | 'image' | 'video' | 'audio' | 'pdf'
  | 'model3d' | 'document' | 'archive' | 'data' | 'link'

export interface Attachment {
  /** Where the data lives. Can be https://, http://, file://, or any
   * scheme the host can resolve. mu-api also supports a mu:// scheme
   * that resolves through its /content/* mount. */
  uri: string
  /** Visual / viewer bucket. Auto-derived from URI extension when added,
   * but caller may override. */
  kind: AttachmentKind
  /** Optional fine-grained MIME type, e.g. 'image/png'. Display only. */
  mimeType?: string
  /** Display label; falls back to URI basename. */
  label?: string
  /** Bytes, if known. Display only. */
  size?: number
  /** Hex sha256, if known. Display only / dedup hint. */
  sha256?: string
  /** ISO8601 timestamp the attachment was added. */
  addedAt?: string
}

/** Infer a kind bucket from a URI's extension. Falls back to 'link'. */
export function inferAttachmentKind(uri: string): AttachmentKind {
  const lower = uri.toLowerCase().split('?')[0].split('#')[0]
  if (/\.(jpe?g|png|gif|webp|svg|bmp|avif|heic)$/.test(lower)) return 'image'
  if (/\.(mp4|webm|mov|m4v|avi|mkv)$/.test(lower)) return 'video'
  if (/\.(mp3|wav|ogg|m4a|flac|opus)$/.test(lower)) return 'audio'
  if (/\.pdf$/.test(lower)) return 'pdf'
  if (/\.(glb|gltf|usdz|obj|fbx|stl|3ds)$/.test(lower)) return 'model3d'
  if (/\.(md|markdown|txt|json|yaml|yml|csv|tsv|html|xml)$/.test(lower)) return 'document'
  if (/\.(zip|tar|gz|tgz|bz2|7z|rar)$/.test(lower)) return 'archive'
  if (/\.(parquet|arrow|h5|npy|npz)$/.test(lower)) return 'data'
  return 'link'
}

/** Pull a display label from a URI (basename or last path segment). */
export function basenameFromUri(uri: string): string {
  try {
    const u = new URL(uri)
    const segs = u.pathname.split('/').filter(Boolean)
    return segs[segs.length - 1] || u.host
  } catch {
    /* Bare path (e.g. mu://docs/foo.pdf without proper URL parsing) */
    const segs = uri.split('/').filter(Boolean)
    return segs[segs.length - 1] || uri
  }
}

export interface RelatedMemory {
  direction: 'in' | 'out'
  connectedId: number
  type: string
  strength: number
  connectedTopic: string | null
  connectedImportance: number | null
}

export interface MemoryDetail {
  id: number
  topic: string | null
  preview: string
  contentLength: number
  importance: number | null
  tags: string[]
  memoryType: string | null
  agentId: string | null
  timestamp: string
  lastAccessed: string | null
  accessCount: number
  hasEmbedding: boolean
  relationships: { out: number; in: number }
  /** Top-strength related memories (capped at 60 by the API). */
  relationshipsList?: RelatedMemory[]
  /** URI-referenced media attachments (read from metadata.attachments). */
  attachments?: Attachment[]
  v2Enrichment: Record<string, unknown> | null
}

export interface Edge {
  source: number
  target: number
  type: string
  strength: number
}

export interface CreateMemoryInput {
  content: string
  topic?: string | null
  importance?: number | null
  tags?: string[]
  memoryType?: string | null
  agentId?: string | null
}

export interface UpdateMemoryInput {
  content?: string
  topic?: string | null
  importance?: number | null
  tags?: string[]
  memoryType?: string | null
}

export interface CreateMemoryResult {
  id: number
  topic: string | null
  importance: number | null
  agentId: string | null
  timestamp: string
  hasEmbedding: false
  embeddingHint: string
}

export interface ContextChunk {
  id: number
  topic: string | null
  importance: number | null
  agentId: string | null
  contentLength: number
  preview: string
  contentFull: string
  contentTruncated: boolean
  timestamp: string
  parentId: number | null
  partN: number | null
  partTotal: number | null
  matchedBy: 'parent' | 'stem'
}

export interface ContextResponse {
  targetId: number
  stem: string | null
  parentRoot: number
  strategy: string
  chunks: ContextChunk[]
  total: number
}

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api'

/**
 * Resolve auth headers for mu-api calls. Reads from mcoAuth state.
 *
 *   X-Tenant-Id     — when an MCO tenant is resolved, sent so mu-api can
 *                     scope queries via its per-request RLS GUC. When
 *                     missing, mu-api defaults to tenant 1 (operator).
 *   Authorization   — when MCO bearer is held, sent for future writes
 *                     once mu-api gains auth-required mutations. Read
 *                     endpoints currently ignore it.
 *
 * Reads from the live mcoAuth $state — no need to subscribe; the helper
 * is called per-fetch so the latest values land on each request.
 */
import { mco, noteBearerRejected } from './mcoAuth.svelte'

function authHeaders(): Record<string, string> {
  const h: Record<string, string> = {}
  /* the memory being VIEWED (ticket #9: an agent's, by grant) — else the user's own */
  const tid = mco.viewing?.id ?? mco.own?.id ?? mco.tenant?.id
  if (tid != null) h['X-Tenant-Id'] = String(tid)
  /* Prefer the reactive state; fall back to localStorage when the state
   * hasn't been hydrated yet. This defends against the boot-order race
   * where Constellation's onMount fires loadLayout before startMcoAuth
   * has populated mco.token from localStorage — symptom was 401s with
   * no Authorization header even though a valid bearer was cached.
   * The two sources should always agree once mcoAuth settles. */
  let token = mco.token
  if (!token) {
    try { token = localStorage.getItem('mco_access_token') } catch { /* private mode */ }
  }
  if (token) h['Authorization'] = `Bearer ${token}`
  return h
}

function mergeHeaders(init: RequestInit | undefined): RequestInit {
  const auth = authHeaders()
  if (Object.keys(auth).length === 0) return init ?? {}
  const existing = init?.headers ?? {}
  /* Browser Headers init can be Headers | Record<string, string> | [string,string][].
   * Flatten via `new Headers(existing)` and merge. */
  const merged = new Headers(existing as HeadersInit)
  for (const [k, v] of Object.entries(auth)) merged.set(k, v)
  return { ...(init ?? {}), headers: merged }
}

/**
 * Live API status. Components can read it to render an offline banner.
 * Updated on every fetch — any successful response sets `online=true`,
 * any failure sets `online=false` with the reason.
 */
export const apiStatus = $state<{
  online: boolean
  reason: string | null
  memoriesWithEmbeddings: number | null
  /** True when the API server has CRUD endpoints enabled (env var set). */
  writesEnabled: boolean
  lastChecked: number | null
  /** Number of in-flight requests currently retrying after a failure. Lets
   * the UI show "RETRYING (n)" instead of going straight to offline. */
  retrying: number
  /** Time of last consecutive-failure threshold breach. Lets components
   * differentiate "transient blip" from "really down". */
  lastHardFailure: number | null
}>({
  online: false,
  reason: null,
  memoriesWithEmbeddings: null,
  writesEnabled: false,
  lastChecked: null,
  retrying: 0,
  lastHardFailure: null,
})

function markOnline(extra?: Partial<typeof apiStatus>): void {
  apiStatus.online = true
  apiStatus.reason = null
  apiStatus.lastChecked = Date.now()
  if (extra?.memoriesWithEmbeddings !== undefined) {
    apiStatus.memoriesWithEmbeddings = extra.memoriesWithEmbeddings
  }
}

function markOffline(reason: string): void {
  apiStatus.online = false
  apiStatus.reason = reason
  apiStatus.lastChecked = Date.now()
  apiStatus.lastHardFailure = Date.now()
}

/**
 * Resilient fetch wrapper. 3 attempts with exponential backoff (200ms, 600ms,
 * 1400ms). Per-route timeout via AbortController so a hung connection fails
 * fast and the next attempt fires. Updates `apiStatus.retrying` so the UI
 * can show "RETRYING (n)" while the loop runs.
 *
 * Returns the Response on first success, throws after the final failure.
 * Distinguishes 4xx (don't retry, client error) from 5xx / network (retry).
 */
async function fetchWithRetry(
  url: string,
  init: RequestInit = {},
  opts: { attempts?: number; timeoutMs?: number; label?: string } = {},
): Promise<Response> {
  const { attempts = 3, timeoutMs = 15_000, label = 'fetch' } = opts
  let lastErr: Error | null = null

  for (let i = 0; i < attempts; i++) {
    if (i > 0) {
      apiStatus.retrying = i
      const backoff = 200 * Math.pow(2, i - 1)  /* 200, 400, 800 */
      await new Promise((r) => setTimeout(r, backoff))
    }
    try {
      const ctrl = new AbortController()
      const t = setTimeout(() => ctrl.abort(), timeoutMs)
      /* Merge auth headers (X-Tenant-Id, Authorization) into every call. */
      const initWithAuth = mergeHeaders({ ...init, signal: ctrl.signal })
      const res = await fetch(url, initWithAuth)
      clearTimeout(t)

      if (res.status >= 400 && res.status < 500) {
        /* Client errors are not retryable. Return the response so the
         * caller can interpret 404, 403, etc. */
        if (res.status === 401) {
          /* Bearer expired/rejected mid-session — flip auth to 'expired' so
           * McoAuthGate re-prompts sign-in. Without this a 401 on the cheap
           * /tasks/version poll was swallowed to null and read as "nothing
           * changed", freezing the DAG on its last good snapshot silently. */
          noteBearerRejected()
        }
        apiStatus.retrying = 0
        return res
      }
      if (!res.ok) {
        lastErr = new Error(`HTTP ${res.status} on ${label}`)
        continue
      }
      apiStatus.retrying = 0
      return res
    } catch (err) {
      lastErr = err as Error
      if ((err as Error).name === 'AbortError') {
        lastErr = new Error(`timeout (${timeoutMs}ms) on ${label}`)
      }
    }
  }
  apiStatus.retrying = 0
  throw lastErr ?? new Error(`fetch failed: ${label}`)
}

export async function checkHealth(): Promise<void> {
  try {
    /* Health check uses a tighter timeout — if it can't get a sub-5s
     * answer something is genuinely wrong. */
    const res = await fetchWithRetry(`${API_BASE}/health`, {}, {
      attempts: 2, timeoutMs: 5_000, label: 'health',
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const json = (await res.json()) as {
      ok: boolean
      memoriesWithEmbeddings?: number
      writesEnabled?: boolean
    }
    if (json.ok) {
      markOnline({ memoriesWithEmbeddings: json.memoriesWithEmbeddings ?? null })
      apiStatus.writesEnabled = json.writesEnabled === true
    } else {
      markOffline('api reported not ok')
    }
  } catch (err) {
    markOffline((err as Error).message)
  }
}

/** Ticket #9 — the tenants this person may view (their agents' memory, by grant): fills mco.viewable; a viewed tenant no
 *  longer granted is let go (back to their own). Quiet on failure: the switcher just doesn't show. */
export async function fetchViewable(): Promise<void> {
  try {
    const res = await fetch(`${API_BASE}/tenants/viewable`, mergeHeaders(undefined))
    if (!res.ok) { mco.viewable = []; return }
    const j = await res.json() as { own?: { id: number; name: string }; viewable?: { id: number; name: string; agentName: string | null; access: string }[] }
    if (j.own) mco.own = { id: Number(j.own.id), slug: '', name: String(j.own.name) }
    mco.viewable = (j.viewable ?? []).map((t) => ({ id: Number(t.id), slug: '', name: String(t.name), agentName: t.agentName ?? undefined, access: t.access }))
    if (mco.viewing && !mco.viewable.some((t) => t.id === mco.viewing!.id)) mco.viewing = null
  } catch {
    mco.viewable = []
  }
}

export async function fetchLayout(query: LayoutQuery = {}): Promise<LayoutResponse> {
  const params = new URLSearchParams()
  if (query.projection) params.set('projection', query.projection)
  if (query.limit) params.set('limit', String(query.limit))
  if (query.agentId) params.set('agent_id', query.agentId)
  if (query.importanceMin !== undefined) params.set('importance_min', String(query.importanceMin))
  if (query.importanceMax !== undefined) params.set('importance_max', String(query.importanceMax))

  /* UMAP at 5K-50K points = 30s to 5min; PCA is 1-10s. Single attempt:
   * retrying an expensive query just makes the wait worse and piles up
   * pool connections. If the FIRST attempt fails, the API is genuinely
   * down and the user should see that immediately. */
  const isUmap = query.projection === 'umap'
  const perAttemptMs = isUmap ? 300_000 : 60_000

  try {
    const res = await fetchWithRetry(
      `${API_BASE}/layout?${params.toString()}`,
      {},
      { attempts: 1, timeoutMs: perAttemptMs, label: 'layout' },
    )
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    markOnline()
    return (await res.json()) as LayoutResponse
  } catch (err) {
    markOffline((err as Error).message)
    console.warn(`[mu-app] /api/layout unavailable after retries: ${(err as Error).message}`)
    return mockLayout(query.projection ?? 'pca', query.limit ?? 5000)
  }
}

export async function fetchMemoryDetail(id: number): Promise<MemoryDetail | null> {
  try {
    const res = await fetchWithRetry(`${API_BASE}/memory/${id}`, {}, {
      attempts: 3, timeoutMs: 10_000, label: `memory/${id}`,
    })
    if (res.status === 404) {
      markOnline()
      return null
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    markOnline()
    return (await res.json()) as MemoryDetail
  } catch (err) {
    markOffline((err as Error).message)
    console.warn(`[mu-app] /api/memory/${id} unavailable after retries: ${(err as Error).message}`)
    return mockMemoryDetail(id)
  }
}

export interface MemoryContentSlice {
  id: number
  from: number
  to: number
  length: number
  fullLength: number
  content: string
}

export async function fetchMemoryContent(
  id: number,
  from = 0,
  to: number | null = null,
): Promise<MemoryContentSlice | null> {
  const params = new URLSearchParams()
  if (from > 0) params.set('from', String(from))
  if (to != null) params.set('to', String(to))
  const qs = params.toString() ? '?' + params.toString() : ''
  try {
    /* Per-chunk content can be hundreds of KB; allow generous timeout. */
    const res = await fetchWithRetry(`${API_BASE}/memory/${id}/content${qs}`, {}, {
      attempts: 2, timeoutMs: 30_000, label: `memory/${id}/content`,
    })
    if (res.status === 404) { markOnline(); return null }
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    markOnline()
    return (await res.json()) as MemoryContentSlice
  } catch (err) {
    markOffline((err as Error).message)
    console.warn(`[mu-app] memory content fetch failed: ${(err as Error).message}`)
    return null
  }
}

export async function fetchContext(id: number): Promise<ContextResponse | null> {
  try {
    const res = await fetchWithRetry(`${API_BASE}/memory/${id}/context`, {}, {
      attempts: 2, timeoutMs: 15_000, label: `context/${id}`,
    })
    if (res.status === 404) { markOnline(); return null }
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    markOnline()
    return (await res.json()) as ContextResponse
  } catch (err) {
    markOffline((err as Error).message)
    console.warn(`[mu-app] /api/memory/${id}/context unavailable: ${(err as Error).message}`)
    return null
  }
}

export async function fetchEdges(ids: number[], strengthMin = 0.3, limit = 2000): Promise<Edge[]> {
  if (ids.length === 0) return []
  const capped = ids.slice(0, 50_000)  /* mu-api caps server-side; we just cap defensively */

  /* POST body, not GET querystring. With 50K IDs the URL ran to 250KB+
   * and blew past Vite's 8KB header limit (HTTP 431). The route stays
   * read-only; POST is just the transport. */
  try {
    const res = await fetchWithRetry(`${API_BASE}/edges`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: capped, strengthMin, limit }),
    }, {
      attempts: 2, timeoutMs: 30_000, label: 'edges',
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    markOnline()
    const json = (await res.json()) as { edges: Edge[] }
    return json.edges
  } catch (err) {
    markOffline((err as Error).message)
    console.warn(`[mu-app] /api/edges unavailable after retries: ${(err as Error).message}`)
    return mockEdges(capped, limit)
  }
}

/**
 * Thrown when the user tries to write but the API has writes disabled.
 * The UI can catch this and surface a helpful banner instead of a generic
 * error toast.
 */
export class WritesDisabledError extends Error {
  constructor() { super('writes-disabled'); this.name = 'WritesDisabledError' }
}

export async function createMemory(input: CreateMemoryInput): Promise<CreateMemoryResult> {
  const res = await fetch(`${API_BASE}/memory`, mergeHeaders({
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  }))
  if (res.status === 403) throw new WritesDisabledError()
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`)
  markOnline()
  return (await res.json()) as CreateMemoryResult
}

export async function updateMemory(id: number, patch: UpdateMemoryInput): Promise<MemoryDetail> {
  const res = await fetch(`${API_BASE}/memory/${id}`, mergeHeaders({
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  }))
  if (res.status === 403) throw new WritesDisabledError()
  if (res.status === 404) throw new Error('not found')
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`)
  markOnline()
  /* The PATCH response shape is a subset (no preview / relationships).
   * Re-fetch the full record so the panel stays consistent. */
  const detail = await fetchMemoryDetail(id)
  if (!detail) throw new Error('updated memory could not be re-fetched')
  return detail
}

export async function addAttachment(id: number, att: Attachment): Promise<MemoryDetail> {
  const res = await fetch(`${API_BASE}/memory/${id}/attachments`, mergeHeaders({
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(att),
  }))
  if (res.status === 403) throw new WritesDisabledError()
  if (res.status === 404) throw new Error('memory not found')
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`)
  markOnline()
  /* Re-fetch full record so the panel state stays consistent. */
  const detail = await fetchMemoryDetail(id)
  if (!detail) throw new Error('post-add fetch failed')
  return detail
}

export async function removeAttachment(id: number, index: number): Promise<MemoryDetail> {
  const res = await fetch(`${API_BASE}/memory/${id}/attachments/${index}`, mergeHeaders({
    method: 'DELETE',
  }))
  if (res.status === 403) throw new WritesDisabledError()
  if (res.status === 404) throw new Error('memory not found')
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`)
  markOnline()
  const detail = await fetchMemoryDetail(id)
  if (!detail) throw new Error('post-remove fetch failed')
  return detail
}

export async function deleteMemory(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/memory/${id}`, mergeHeaders({ method: 'DELETE' }))
  if (res.status === 403) throw new WritesDisabledError()
  if (res.status === 404) return  /* idempotent — already gone is success */
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`)
  markOnline()
}

function mockMemoryDetail(id: number): MemoryDetail {
  return {
    id,
    topic: '(demo / api offline)',
    preview: '[mock data — start mu-api on :3457 to see real content. Check Vite proxy target in vite.config.ts if api is running but this still shows.]',
    contentLength: 0,
    importance: null,
    tags: ['demo'],
    memoryType: null,
    agentId: 'mock',
    timestamp: new Date().toISOString(),
    lastAccessed: null,
    accessCount: 0,
    hasEmbedding: false,
    relationships: { in: 0, out: 0 },
    relationshipsList: [],
    v2Enrichment: null,
  }
}

function mockEdges(ids: number[], limit: number): Edge[] {
  /* Connect each point to ~3 nearest in the id list — gives the layer
   * something to render in offline mode. */
  const out: Edge[] = []
  for (let i = 0; i < ids.length && out.length < limit; i++) {
    const a = ids[i]
    const b = ids[(i + 1) % ids.length]
    const c = ids[(i + 7) % ids.length]
    out.push({ source: a, target: b, type: 'mock-near', strength: 0.7 })
    if (a !== c) out.push({ source: a, target: c, type: 'mock-far', strength: 0.4 })
  }
  return out.slice(0, limit)
}

/** Deterministic 5K-point mock used when the API is offline. */
export function mockLayout(
  projection: 'umap' | 'tsne' | 'pca' | 'force',
  n: number,
): LayoutResponse {
  const rng = mulberry32(0xC0FFEE)
  const clusters = 8
  const centers: Array<[number, number, number]> = []
  for (let c = 0; c < clusters; c++) {
    const t = (c / clusters) * Math.PI * 2
    centers.push([Math.cos(t) * 40, Math.sin(t) * 40, (c - clusters / 2) * 5])
  }

  const points: LayoutPoint[] = []
  for (let i = 0; i < n; i++) {
    const c = i % clusters
    const [cx, cy, cz] = centers[c]
    const r = rng() * 12
    const a = rng() * Math.PI * 2
    const z = cz + (rng() - 0.5) * 4
    points.push({
      id: i + 1,
      x: cx + Math.cos(a) * r,
      y: cy + Math.sin(a) * r,
      z,
      cluster_id: c,
      importance: Math.floor(rng() * 25),
      agent_id: i % 2 === 0 ? 'claude-nexus' : 'claude-nexus-v2',
      topic: `mock-topic-${c}`,
    })
  }

  return {
    projection,
    points,
    total: n,
    computedAt: new Date().toISOString(),
  }
}

function mulberry32(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (s + 0x6D2B79F5) >>> 0
    let t = s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/* ── Task graph (canonical task graph in nexus_paths, via mu-api /api/tasks/*)
 * ─────────────────────────────────────────────────────────────────────────
 * Read-only live view feeding the "Tasks" module. The task graph is OSM-style:
 * project_track ways = tracks, milestone nodes = milestones, depends_on
 * relations = edges. mu-api reads a SEPARATE pool (nexus_paths), no tenancy.
 */

export type MilestoneState = 'done' | 'active' | 'next' | 'waiting' | 'planned'

export interface TaskTrack {
  way_id: number
  slug: string
  name: string
  created: string | null
  node_refs: number[]
}
export interface TaskMilestone {
  id: number
  state: string | null
  title: string | null
  project: string | null
  description: string | null
  completed_at: string | null
  waiting_on: string | null
  has_embedding: boolean
}
export interface TaskDep {
  rel_id: number
  relation_type: 'depends_on' | 'conflict' | 'intersection'
  from_id: number
  to_id: number
}
export interface TaskGraphData {
  generated_at: string
  tracks: TaskTrack[]
  milestones: TaskMilestone[]
  deps: TaskDep[]
}

/** Full canonical task graph (tracks + milestones + deps) in one round trip. */
export async function fetchTaskGraph(): Promise<TaskGraphData> {
  const res = await fetchWithRetry(`${API_BASE}/tasks/graph`, {}, {
    attempts: 3, timeoutMs: 20_000, label: 'tasks/graph',
  })
  if (!res.ok) throw new Error(`tasks/graph HTTP ${res.status}`)
  markOnline()
  return (await res.json()) as TaskGraphData
}

/** Cheap freshness signature — poll this, refetch the graph only on change. */
export async function fetchTaskVersion(): Promise<string | null> {
  try {
    const res = await fetchWithRetry(`${API_BASE}/tasks/version`, {}, {
      attempts: 2, timeoutMs: 8_000, label: 'tasks/version',
    })
    if (!res.ok) {
      /* Don't let a failed freshness check pass silently. A 401 already
       * re-prompts auth (fetchWithRetry → noteBearerRejected); for any other
       * failure flip the status offline so the StatusBar shows the graph is no
       * longer live, instead of implying the cached counts are still current. */
      markOffline(`tasks/version HTTP ${res.status}`)
      return null
    }
    markOnline()
    const j = (await res.json()) as { sig?: string | null }
    return j.sig ?? null
  } catch (err) {
    markOffline((err as Error).message)
    return null
  }
}

export interface RelatedMemory {
  id: number
  topic: string | null
  content: string | null
  importance: number | null
  agentId: string | null
  timestamp: string | null
  similarity: number
}

/** Multidimensional Sankey fact table over the whole process commons.
 *  One row per (namespace, graph, way, state) + count; the client pivots it
 *  to a Sankey at any chosen subset of dimensions. */
export interface SankeyRow {
  namespace: string
  graph: string
  graph_kind: string
  way_id: number
  way: string
  way_kind: string
  state: string
  count: number
}
export async function fetchTaskSankey(): Promise<{ dimensions: string[]; rows: SankeyRow[] }> {
  const res = await fetchWithRetry(`${API_BASE}/tasks/sankey`, {}, {
    attempts: 2, timeoutMs: 15_000, label: 'tasks/sankey',
  })
  if (!res.ok) throw new Error(`tasks/sankey HTTP ${res.status}`)
  return (await res.json()) as { dimensions: string[]; rows: SankeyRow[] }
}

/** Full process-commons tree (flat records w/ parent) for drill-down. */
export interface TreeRec {
  id: string
  parent: string | null
  kind: 'root' | 'namespace' | 'graph' | 'way' | 'node'
  label: string
  state?: string | null
  meta?: string
}
export async function fetchTaskTree(): Promise<TreeRec[]> {
  const res = await fetchWithRetry(`${API_BASE}/tasks/tree`, {}, {
    attempts: 2, timeoutMs: 20_000, label: 'tasks/tree',
  })
  if (!res.ok) throw new Error(`tasks/tree HTTP ${res.status}`)
  return ((await res.json()) as { nodes: TreeRec[] }).nodes
}

/** Live NEXUS cross-link: most semantically-related memories for a milestone. */
export async function fetchMilestoneMemories(id: number, limit = 8): Promise<RelatedMemory[]> {
  const res = await fetchWithRetry(`${API_BASE}/tasks/milestone/${id}/memories?limit=${limit}`, {}, {
    attempts: 2, timeoutMs: 30_000, label: 'tasks/ms-memories',
  })
  if (!res.ok) throw new Error(`milestone memories HTTP ${res.status}`)
  const j = (await res.json()) as { results?: RelatedMemory[] }
  return j.results ?? []
}

/** Connection metadata for a Sankey flow edge — the skill/procedure/audit
 *  substance the link-click "dives into". */
export interface AuditReceipt {
  actor: string | null
  action_ref: string | null
  result: string | null
  evidence: unknown
  run_id: string | null
  at: string | null
}
export interface EdgeEndpoint {
  tid: string
  kind: string
  label: string
  state?: string | null
  skill?: { runtime?: string; ref?: string } | null
  check?: { kind?: string; assert?: string } | null
  gate?: string | null
  wayKind?: string | null
  graphKind?: string | null
  namespace?: string | null
  visibility?: string | null
}
export interface TaskEdge {
  from: EdgeEndpoint | null
  to: EdgeEndpoint
  relation: string | null
  relationLabel: string | null
  audits: AuditReceipt[]
}
export async function fetchTaskEdge(toTid: string, fromTid?: string | null): Promise<TaskEdge> {
  const qs = new URLSearchParams({ to: toTid })
  if (fromTid) qs.set('from', fromTid)
  const res = await fetchWithRetry(`${API_BASE}/tasks/edge?${qs.toString()}`, {}, {
    attempts: 2, timeoutMs: 15_000, label: 'tasks/edge',
  })
  if (!res.ok) throw new Error(`tasks/edge HTTP ${res.status}`)
  return (await res.json()) as TaskEdge
}

/** The Codex capability graph (skills + processes/standards + edges + reputation)
 *  behind the CODEX lens. */
export interface CodexGraph {
  skills: { id: number; role: string; ref: string; title: string | null; namespace: string | null;
            runs?: number | null; pass_rate?: number | null; dependent_templates?: number | null }[]
  steps: { id: number; ref: string }[]
  ways: { id: number; role: string; ref: string; name: string | null; namespace: string | null; node_refs: number[] }[]
  edges: { type: string; from_type: string; from_id: number; to_type: string; to_id: number }[]
}
export async function fetchCodexGraph(): Promise<CodexGraph> {
  const res = await fetchWithRetry(`${API_BASE}/tasks/codex`, {}, { attempts: 2, timeoutMs: 20_000, label: 'tasks/codex' })
  if (!res.ok) throw new Error(`tasks/codex HTTP ${res.status}`)
  return (await res.json()) as CodexGraph
}
